import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const CreateNewCaseModal = ({ show, onHide, caseNumber, caseId, caseData, currentCase }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    reason: '',
    description: '',
    checkboxes: {
      fullArchTemp: false,
      uber: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    reason: '',
    description: ''
  });
  const [remakeTypes, setRemakeTypes] = useState([]);
  const [fetchingTypes, setFetchingTypes] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [descriptionOptions, setDescriptionOptions] = useState([]);

  // Get userLabId from localStorage
  const userLabId = localStorage.getItem('userLabId') || 'defaultLabId';

  // Fetch remake types when modal opens
  useEffect(() => {
    if (show) {
      fetchRemakeTypes();
      // Reset form when modal opens
      setFormData({
        reason: '',
        description: '',
        checkboxes: {
          fullArchTemp: false,
          uber: false
        }
      });
      setErrors({ reason: '', description: '' });
      setSelectedType(null);
      setDescriptionOptions([]);
    }
  }, [show]);

  const fetchRemakeTypes = async () => {
    try {
      setFetchingTypes(true);
      const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/remake-types?userLabId=${userLabId}`);
      
      if (response.data.success) {
        setRemakeTypes(response.data.data);
      } else {
        setRemakeTypes([]);
      }
    } catch (error) {
      console.error("Error fetching remake types:", error);
      setRemakeTypes([]);
    } finally {
      setFetchingTypes(false);
    }
  };

  // Parse description string into array of options
  const parseDescriptionOptions = (descriptionString) => {
    if (!descriptionString || !descriptionString.trim()) {
      return [];
    }
    
    // Split by comma and parse each part
    const options = descriptionString.split(', ').map(part => {
      const match = part.match(/(.+) \((.+)\)/);
      if (match) {
        return {
          label: match[1],
          value: match[1],
          amount: match[2],
          fullText: part
        };
      }
      return {
        label: part,
        value: part,
        amount: '0',
        fullText: part
      };
    }).filter(option => option.label.trim());
    
    return options;
  };

  const handleReasonChange = (e) => {
    const selectedReason = e.target.value;
    
    // Find the selected remake type to get its description
    const type = remakeTypes.find(type => type.reason === selectedReason);
    
    setSelectedType(type);
    
    if (type) {
      // Parse description into individual options
      const options = parseDescriptionOptions(type.description);
      setDescriptionOptions(options);
      
      // If there's only one description option, auto-select it
      if (options.length === 1) {
        setFormData({
          ...formData,
          reason: selectedReason,
          description: options[0].fullText
        });
      } else {
        // Reset description when reason changes
        setFormData({
          ...formData,
          reason: selectedReason,
          description: ''
        });
      }
    } else {
      setDescriptionOptions([]);
      setFormData({
        ...formData,
        reason: selectedReason,
        description: ''
      });
    }
    
    if (selectedReason.trim()) {
      setErrors(prev => ({ ...prev, reason: '' }));
    }
  };

  const handleDescriptionChange = (e) => {
    const selectedDescription = e.target.value;
    
    setFormData({
      ...formData,
      description: selectedDescription
    });
    
    if (selectedDescription.trim()) {
      setErrors(prev => ({ ...prev, description: '' }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      checkboxes: {
        ...formData.checkboxes,
        [name]: checked
      }
    });
  };

  const handleCancel = () => {
    setFormData({
      reason: '',
      description: '',
      checkboxes: {
        fullArchTemp: false,
        uber: false
      }
    });
    setErrors({ reason: '', description: '' });
    setSelectedType(null);
    setDescriptionOptions([]);
    onHide();
  };

  const handleSave = async () => {
    // Validate
    const newErrors = { reason: '', description: '' };
    let isValid = true;

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
      isValid = false;
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      // Determine the correct case ID/number to fetch
      const lookupValue = caseId || caseNumber;
      
      if (!lookupValue) {
        throw new Error('Case ID or Case Number is missing');
      }

      // Fetch the full case data from the API
      const caseResponse = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${lookupValue}`
      );
      const previousCase = caseResponse.data;

      // Store remake metadata
      const remakeMetadata = {
        previousCaseId: previousCase.case_id || caseId,
        previousCaseNumber: previousCase.caseNumber || caseNumber,
        reason: formData.reason,
        description: formData.description,
        fullArchTemp: formData.checkboxes.fullArchTemp,
        uber: formData.checkboxes.uber,
        // Include previous case data for auto-filling
        previousCaseData: {
          account_holder: previousCase.account_holder,
          first_name: previousCase.first_name,
          last_name: previousCase.last_name,
          provider_name: previousCase.provider_name,
          patientChart: previousCase.patientChart,
          birth_date: previousCase.birth_date,
          category_holder: previousCase.category_holder,
        }
      };

      // Close the modal
      handleCancel();

      // Navigate to AccountDetails with case data and remake metadata
      navigate(`/accountDetails/${previousCase.case_id || caseId}`, {
        state: {
          caseData: previousCase,
          isRemake: true,
          remakeMetadata: remakeMetadata,
        }
      });

    } catch (error) {
      console.error('Error creating new case:', error);
      alert(error.response?.data?.message || 'Error creating new case. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      centered
      backdrop="static"
      dialogClassName="custom-modal-dialog"
    >
      <style>
        {`
          .custom-modal-dialog {
            width: 500px !important;
            max-width: 500px !important;
            margin: 0 auto !important;
          }
          .custom-modal-dialog .modal-content {
            border-radius: 8px;
          }
          .close-btn-custom {
            opacity: 1;
            font-size: 1.5rem;
            font-weight: 300;
            color: #fff;
            transition: all 0.3s ease;
          }
          .close-btn-custom:hover {
            opacity: 0.8;
            transform: scale(1.1);
          }
          .form-label-custom {
            font-weight: 600;
            color: #000;
            margin-bottom: 8px;
            font-size: 14px;
            width: 120px;
            flex-shrink: 0;
          }
          .form-group-row {
            display: flex;
            align-items: flex-start;
            margin-bottom: 20px;
            gap: 20px;
          }
          .form-control-custom {
            flex: 1;
            border: 1px solid #ced4da;
            border-radius: 4px;
            padding: 6px 12px;
            font-size: 14px;
            background-color: #fff;
            width: 250px;
            height: 35px;
          }
          .form-select-custom {
            flex: 1;
            border: 1px solid #ced4da;
            border-radius: 4px;
            padding: 6px 12px;
            font-size: 14px;
            background-color: #fff;
            width: 100%;
            height: 35px;
          }
          textarea.form-control-custom {
            min-height: 35px;
            height: 35px;
            resize: none;
          }
          .checkbox-group {
            border-top: 2px solid #004aad;
            padding-top: 15px;
            margin-top: 10px;
            background-color: #f5f5f5;
            padding: 15px;
            border-radius: 4px;
          }
          .custom-checkbox {
            margin-bottom: 10px;
            padding-left: 20px;
          }
          .custom-checkbox .form-check-input {
            margin-right: 10px;
          }
          .custom-checkbox .form-check-label {
            font-weight: normal;
            color: #333;
          }
          .button-group {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
          }
          .error-text {
            color: red;
            font-size: 12px;
            margin-top: 5px;
            margin-left: 140px;
          }
          .description-info {
            font-size: 12px;
            color: #666;
            margin-top: 5px;
          }
          .amount-badge {
            display: inline-block;
            background-color: #e9ecef;
            color: #495057;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            margin-left: 8px;
          }
        `}
      </style>
     
      <Modal.Header
        closeButton
        style={{
          backgroundColor: '#004aad',
          borderBottom: 'none',
          padding: '16px 20px',
          borderRadius: '8px 8px 0 0'
        }}
        closeVariant="white"
      >
        <Modal.Title style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#ffffff'
        }}>
          Create Next Case from #{caseNumber || currentCase?.caseNumber}
        </Modal.Title>
      </Modal.Header>
     
      <Modal.Body style={{
        padding: '24px 20px',
        overflowY: 'auto',
      }}>
        <Form>
          {/* Reason For Case - Dropdown Select */}
          <div className="form-group-row">
            <Form.Label className="form-label-custom">
              Reason <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <div style={{ flex: 1 }}>
              {fetchingTypes ? (
                <div style={{ padding: '8px', color: '#666' }}>Loading reasons...</div>
              ) : (
                <>
                  <Form.Select
                    name="reason"
                    value={formData.reason}
                    onChange={handleReasonChange}
                    className="form-select-custom"
                    style={{
                      width: '100%',
                      height: '35px'
                    }}
                  >
                    <option value="">Select Reason</option>
                    {remakeTypes.map((type) => (
                      <option key={type._id} value={type.reason}>
                        {type.reason}
                      </option>
                    ))}
                  </Form.Select>
                  {selectedType && descriptionOptions.length > 0 && (
                    <div className="description-info">
                      {descriptionOptions.length} description(s) available
                    </div>
                  )}
                </>
              )}
              {errors.reason && (
                <div className="error-text">{errors.reason}</div>
              )}
            </div>
          </div>
 
          {/* Description - Dropdown Select */}
          <div className="form-group-row">
            <Form.Label className="form-label-custom">
              Description <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <div style={{ flex: 1 }}>
              {!formData.reason ? (
                <div style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  color: '#6c757d',
                  fontSize: '14px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Select a reason first
                </div>
              ) : descriptionOptions.length === 0 ? (
                <div style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  color: '#6c757d',
                  fontSize: '14px',
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  No description available for this reason
                </div>
              ) : (
                <>
                  <Form.Select
                    name="description"
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    className="form-select-custom"
                    style={{
                      width: '100%',
                      height: '35px'
                    }}
                  >
                    <option value="">Select Description</option>
                    {descriptionOptions.map((option, index) => (
                      <option key={index} value={option.fullText}>
                        {option.label}
                        {option.amount !== '0' && (
                          ` - £${option.amount}`
                        )}
                      </option>
                    ))}
                  </Form.Select>
                  {formData.description && (
                    <div className="description-info" style={{ marginTop: '8px' }}>
                      {descriptionOptions.map((option, index) => {
                        if (option.fullText === formData.description) {
                          return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                              <span>Selected: {option.label}</span>
                              {option.amount !== '0' && (
                                <span className="amount-badge">Amount: £{option.amount}</span>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </>
              )}
              {errors.description && (
                <div className="error-text">{errors.description}</div>
              )}
            </div>
          </div>
        </Form>
      </Modal.Body>
     
      <Modal.Footer style={{
        backgroundColor: '#fff',
        borderTop: 'none',
        padding: '16px 20px'
      }}>
        <div className="button-group">
          <Button
            variant="secondary"
            onClick={handleCancel}
            style={{
              backgroundColor: '#6c757d',
              borderColor: 'transparent',
              color: '#fff',
              padding: '6px 25px',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '4px'
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#5a6268')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#6c757d')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading || !formData.reason || !formData.description}
            style={{
              backgroundColor: '#004aad',
              borderColor: 'transparent',
              padding: '6px 25px',
              fontSize: '14px',
              fontWeight: '500',
              borderRadius: '4px'
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#003a8c')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#004aad')}
          >
            {loading ? 'Creating...' : 'Save & Continue'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateNewCaseModal;